import React, { useEffect, useState } from 'react';

import { Box, Divider, Link, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { ImageAvatar } from 'components/Avatar';
import { ListCard } from 'components/Card';
import { Status as StatusComponent } from 'components/Status';

import { IconWrapper } from 'components/Icon';

import { useAuth } from 'lib/auth';
import { paths } from 'routes';

import { StarButton } from './StarButton';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en'; // Import a base locale, for example 'en'
import { formatJobAdLocation, formatJobAdLocationShort, getJobAdLocations } from 'utils/jobAdLocations';

dayjs.extend(relativeTime);
dayjs.extend(utc);

// Get the existing 'en' locale data
const enLocale = dayjs.Ls.en;

// Define a custom locale to remove "ago"
const customRelativeTime = {
  ...enLocale, // Start with the full 'en' locale data
  relativeTime: {
    ...enLocale.relativeTime, // Extend existing relativeTime if needed
    future: "in %s",
    past: "%s", // Changed from "%s ago" to "%s"
    s: "a few seconds",
    m: "a minute",
    mm: "%d minutes",
    h: "an hour",
    hh: "%d hours",
    d: "a day",
    dd: "%d days",
    M: "a month",
    MM: "%d months",
    y: "a year",
    yy: "%d years"
  }
};

dayjs.locale('custom', customRelativeTime); // Register and use the custom locale

const userTimeZone = dayjs.tz.guess();

const getFirstSentence = (text) => {
  if (!text) return '';
  const firstSentenceMatch = text.match(/[^.!?]+[.!?]/);
  return firstSentenceMatch ? firstSentenceMatch[0].trim() : text.trim();
};

const OWNER_TYPE_LABELS = {
  Intermediator: 'Intermediator',
  Framework: 'Framework Agreement',
  DPS: 'DPS',
  Direct: 'Direct',
};

export const ListItem = ({
  ad_id,
  headline,
  reference_number,
  status,
  end_date,
  buyer_id,
  buyer_name,
  buyer_slug,
  buyer_hidden,
  supplier_id,
  supplier_slug,
  total_no_versions,
  buyer_logo_url,
  locations,
  places,
  supplier_name,
  supplier_logo_url,
  last_day_to_respond,
  role,
  competence_areas,
  created_on,
  description,
  experience_level,
  owner_type,
  hide_buyer,
  is_starred,
  assigned_user_id,
  onStarChange,
}) => {
  const { account, user } = useAuth();
  const theme = useTheme();

  const adjustedDate = dayjs.utc(created_on).tz(userTimeZone, true);
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeAgo(adjustedDate.fromNow());
    }, 60000);

    setTimeAgo(adjustedDate.fromNow());

    return () => clearInterval(intervalId);
  }, [adjustedDate]);

  const displayLocations = getJobAdLocations({ locations, places });

  const locationsTooltip = (
    <>
      {displayLocations.map((loc) => (
        <div key={loc.id}>{formatJobAdLocation(loc)}</div>
      ))}
    </>
  );

  const locationsBaseText = displayLocations.length === 1 ? formatJobAdLocationShort(displayLocations[0]) : 'Multiple';

  let locationsDisplayText;
  if (locationsBaseText.length > 10) {
    locationsDisplayText = `${locationsBaseText.slice(0, 10)}...`;
  } else {
    locationsDisplayText = `${locationsBaseText}`;
  }

  const areasTooltip = (
    <>
      {competence_areas?.map((area) => (
        <div key={area.id}>{area.name}</div>
      ))}
    </>
  );

  const areasBaseText = competence_areas ? competence_areas.length === 1 ? `${competence_areas[0]?.name}` : 'Multiple' : '';

  let areasDisplayText;
  if (areasBaseText.length > 15) {
    areasDisplayText = `${areasBaseText.slice(0, 15)}...`;
  } else {
    areasDisplayText = `${areasBaseText}`;
  }

  const buyer_url = (account.super_admin || user?.Comapny?.company_id === buyer_id ) ? paths.buyersDetailsView(buyer_id) : buyer_slug?.length > 0 ? paths.public.companyPublicProfile("buyer", buyer_slug) : "";
  const supplier_url = (account.super_admin || user?.Comapny?.company_id === supplier_id ) ? paths.suppliersDetailsView(supplier_id) : supplier_slug?.length > 0 ? paths.public.companyPublicProfile("supplier", supplier_slug) : "";

  return (
    <ListCard
      addDefaultStyling = {false}
      sx={{
        borderRadius: 1,
        borderColor:"border.jobAd",
        flexDirection: "row",
        px: 2,
        py: 2,
        gap: 2,
        boxShadow: 'none',
      }}
    >
      <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1,
          flex: "1 0 0",
        }}
      >

        <Stack
          sx={{
            flexDirection: 'row',
            display: 'flex',
            gap: 2,
            width: 1,
            alignItems: 'center',
            flex: '1 0 0',
          }}
        >
          {buyer_url?.length > 0 ? (
            <Link component={RouterLink} to={buyer_url} color="inherit" underline="none">
              <ImageAvatar img={buyer_logo_url || ''} name={buyer_name || ''} anonymous={buyer_hidden} />
            </Link>
          ) : (
            <ImageAvatar img={buyer_logo_url || ''} name={buyer_name || ''} anonymous={buyer_hidden} />
          )}
          <Link
            component={RouterLink}
            to={`${paths.jobAdsDetailsView(ad_id)}`}
            color="inherit"
            underline="none"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: 1,
              gap: 2,
              flex: '1 0 0',
            }}
          >
            <Stack flexDirection={'column'} width={1} flex={'1 0 0'}>
              <Stack direction="row" gap={1} alignItems="center">
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.375rem',
                    fontWeight: 600,
                    lineHeight: 'normal',
                    color: 'text.black',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                  }}
                >
                  {headline}
                </Typography>
                {account.super_admin && hide_buyer && (
                  <Tooltip title="Buyer is hidden">
                    <Box display="flex">
                      <IconWrapper
                        icon="hidden"
                        sx={{
                          mt: -0.4,
                          svg: { stroke: theme.palette.text.secondary, color: theme.palette.text.secondary, strokeWidth: 1.5, width: 16, height: 16 },
                        }}
                        height={16}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
              <Typography
                variant="body3"
                sx={{
                  fontWeight: 400,
                  color: 'text.list',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                }}
              >
                {getFirstSentence(description)}
              </Typography>
            </Stack>
          </Link>
        </Stack>
      </Stack>
      <Stack
        justifyContent={"flex-end"}
        sx={{
          gap: 1.375,
        }}
      >
        <StarButton
          jobAdID={ad_id}
          isStarred={is_starred}
          assignedUserID={assigned_user_id}
          onChange={onStarChange}
          sx={{ alignSelf: 'flex-end', mb: 'auto', mt: -1, mr: -1 }}
        />
        <Stack
          sx={{
            flexDirection: "row",
            gap: 1.375,
          }}
        >
          {owner_type && (
            <Box
              sx={{
                gap: 0.75,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconWrapper
                icon="briefcase"
                sx={{
                  height: 18,
                  width: 18,
                  svg: {
                    stroke: "#000",
                    strokeWidth:1,
                    height: 18,
                    width: 18,
                  }
                }}
                height={18}
              />
              <Typography
                variant="body3"
                sx={{
                  color: "text.black",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
              >
                {OWNER_TYPE_LABELS[owner_type] || owner_type}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              gap: 0.75,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconWrapper
              icon="tag"
              sx={{
                height: 18,
                width: 18,
                svg: {
                  stroke: "#000",
                  strokeWidth:1,
                  height: 18,
                  width: 18,
                }
              }}
              height={18}
            />
            <Typography
              variant="body3"
              sx={{
                color: "text.black",
                fontSize: "0.875rem",
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              {buyer_name}
            </Typography>
          </Box>
          <Box
            sx={{
              gap: 0.75,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconWrapper
              icon="clock"
              sx={{
                height: 18,
                width: 18,
                svg: {
                  stroke: "#000",
                  strokeWidth:1,
                  height: 18,
                  width: 18,
                }
              }}
              height={18}
            />
            <Typography
              variant="body3"
              sx={{
                color: "text.black",
                fontSize: "0.875rem",
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              {timeAgo}
            </Typography>
          </Box>
        </Stack>
        <Box
          sx={{
            width: {xs: 20, md: 24},
            height: {xs: 20, md: 24},
            alignSelf: 'flex-end',
          }}
        >
        {supplier_url?.length > 0 ? (
          <Link
            component={RouterLink}
            to={supplier_url}
            color="inherit"
            underline="none"
          >
            <ImageAvatar img={supplier_logo_url || ''} name={supplier_name || ''} fontSize='1rem'
              sx={{
                width: {xs: 20, md: 24},
                height: {xs: 20, md: 24},
                img : {
                  width: {xs: 20, md: 24},
                  height: {xs: 20, md: 24},
                  overflow: 'hidden',
                },
                fontSize: {xs: "0.875rem", md: "1rem"}
              }}
            />
          </Link>
        ) : (
          <ImageAvatar img={supplier_logo_url || ''} name={supplier_name || ''} fontSize='1rem'
            sx={{
              width: {xs: 20, md: 28},
              height: {xs: 20, md: 28},
              img : {
                width: {xs: 20, md: 28},
                height: {xs: 20, md: 28},
                overflow: 'hidden',
              },
              fontSize: {xs: "0.875rem", md: "1rem"}
            }}
          />
        )}
        </Box>
      </Stack>
      {/* {account.super_admin && (
        <StatusComponent status={status} additionalColor />
      )} */}
    </ListCard>
  );
};
